import { ConfigCatalog, ConfigStatus, MetricSpec, ParamDiff, Person, ThresholdUnit } from './risk-scorecard-config.interface'

/** Rótulos y conversiones del editor de la configuración de la calculadora (plan v4.2 §7.2). */

type TagSeverity = 'success' | 'info' | 'warning' | 'danger' | 'secondary'

export const STATUS_UI: Readonly<Record<ConfigStatus, { label: string; severity: TagSeverity }>> = {
  draft: { label: 'Borrador', severity: 'info' },
  active: { label: 'Activa', severity: 'success' },
  archived: { label: 'Archivada', severity: 'secondary' },
}

export const UNIT_SUFFIX: Readonly<Record<ThresholdUnit, string>> = {
  count: '',
  years: 'años',
  '%': '%',
  hhi: 'HHI',
  pts: 'pts',
  x: 'x',
  days: 'días',
  mxn: '$',
}

//* Los porcentajes se guardan como fracción (0.10) y se capturan como porcentaje (10). Redondeo a 6 decimales para
//* que 0.07 no se vea como 7.000000000000001.
const round6 = (value: number) => Math.round(value * 1e6) / 1e6

export function toDisplay(value: number | null | undefined, unit: ThresholdUnit): number | null {
  if (value === null || value === undefined || !Number.isFinite(value)) return null
  return unit === '%' ? round6(value * 100) : value
}

export function fromDisplay(value: number | null | undefined, unit: ThresholdUnit): number | null {
  if (value === null || value === undefined || !Number.isFinite(value)) return null
  return unit === '%' ? round6(value / 100) : value
}

/** «Ana López», o el correo si no hay nombre. */
export function personName(person: Person | null | undefined): string {
  if (!person) return 'Sistema'
  return [person.names, person.lastNames].filter(Boolean).join(' ').trim() || person.email || 'Usuario no disponible'
}

/** Una métrica tiene umbrales editables si es por tramos o si su regla los usa. */
export function hasTiers(spec: MetricSpec): boolean {
  return spec.kind === 'tiered' || (spec.kind === 'rule' && !!spec.tiers)
}

/**
 * Un cambio del diff en español: «Cobertura del servicio de deuda (DSCR) · umbral de 20 pts: 1.5 → 1.6». Lo que el
 * catálogo no reconoce se muestra con su ruta, nunca se esconde.
 */
export function diffLabel(diff: ParamDiff, catalog: ConfigCatalog | null): string {
  const parts = diff.path.split('.')
  const name = (key: string) => catalog?.metrics[key] ?? catalog?.groups[key] ?? key
  let what = diff.path
  if (parts[0] === 'metrics' && parts[1]) {
    const field = parts.slice(2).join('.')
    const tier = /^tiers\.t(20|15|10)$/.exec(field)
    what = `${name(parts[1])} · ${tier ? `umbral de ${tier[1]} pts` : field === 'weight' ? 'peso' : field === 'direction' ? 'dirección' : field}`
  } else if (parts[0] === 'pillars' && parts[1]) what = `Peso del pilar ${catalog?.pillars[parts[1] as keyof ConfigCatalog['pillars']] ?? parts[1]}`
  else if (parts[0] === 'vetoes' && parts[1]) what = `${catalog?.vetoes[parts[1]] ?? parts[1]} · ${parts[2] === 'band' ? 'banda' : 'activo'}`
  else if (parts[0] === 'bands') what = parts[1] === 'favorableMin' ? 'Corte de Favorable' : 'Corte de Revisar'
  return `${what}: ${shown(diff.previousValue)} → ${shown(diff.newValue)}`
}

function shown(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'boolean') return value ? 'sí' : 'no'
  if (typeof value === 'number') return String(Math.round(value * 1e4) / 1e4)
  return typeof value === 'string' ? value : JSON.stringify(value)
}
